// The admin console's reads (.design-specs/admin-console.plan.md). Person-scoped: no band in the key, because the
// console acts as no band. Every route re-checks on the server (platform admin; a second-factor session in
// production), so these hooks are only ever mounted under the admin shell. Whether a person IS an admin is read
// off `/me` by the router (`sendAdminsToConsole`), not here.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError } from "./apiError.js";
import { problemType } from "./useRelease.js";

export type AdminIntegrationsPanel = components["schemas"]["AdminIntegrationsPanel"];
export type AdminProviderHealth = components["schemas"]["AdminProviderHealth"];
export type AdminAttentionRow = components["schemas"]["AdminAttentionRow"];
export type AdminBandRow = components["schemas"]["AdminBandRow"];
export type AdminSocialRow = components["schemas"]["AdminSocialRow"];
export type AdminPersonRow = components["schemas"]["AdminPersonRow"];
export type AdminPushPanel = components["schemas"]["AdminPushPanel"];
export type AdminAnomaliesPanel = components["schemas"]["AdminAnomaliesPanel"];
export type AdminAnomalyRow = components["schemas"]["AdminAnomalyRow"];
export type AdminBandSync = components["schemas"]["AdminBandSync"];
export type AdminBandSyncRow = components["schemas"]["AdminBandSyncRow"];
export type AdminVendorUsagePanel = components["schemas"]["AdminVendorUsagePanel"];
export type AdminVendorResourceRow = components["schemas"]["AdminVendorResourceRow"];
type AdminTutorialDto = components["schemas"]["AdminTutorialRow"];
/** One tutorial as the console lists it. The generated shape marks every field optional; the server sends them all. */
export interface AdminTutorialRow {
  tutorialId: string;
  title: string;
  position: number;
  youtubeVideoId: string;
  audiences: string[];
  /** ART-311: shown to Pro bands only (an org seat's own Settings lists it too). */
  proOnly: boolean;
  updatedOn: string;
}
const toTutorialRow = (t: AdminTutorialDto): AdminTutorialRow => ({
  tutorialId: t.tutorialId ?? "",
  title: t.title ?? "",
  position: t.position ?? 0,
  youtubeVideoId: t.youtubeVideoId ?? "",
  audiences: t.audiences ?? [],
  proOnly: t.proOnly ?? false,
  updatedOn: t.updatedOn ?? "",
});
export type TutorialInput = components["schemas"]["TutorialInput"];

/** True when the console refused this session for want of a second factor (production only). The remedy is to
 * sign in again with a passkey or the emailed code, so the screen offers that rather than an error card. */
export function isSecondFactorRequired(err: unknown): boolean {
  return problemType(err) === "admin-second-factor-required";
}

/** Every admin read: person-scoped (no band in the key), a short staleTime (a dashboard is read in passing), and no
 * retry — a 403 for want of a second factor will not fix itself on a retry. */
function useAdminRead<T>(key: string, path: "/api/v1/admin/integrations" | "/api/v1/admin/bands" | "/api/v1/admin/socials"
  | "/api/v1/admin/people" | "/api/v1/admin/push" | "/api/v1/admin/anomalies" | "/api/v1/admin/tutorials") {
  const { api } = useApp();
  return useQuery({
    queryKey: ["admin", key],
    queryFn: async () => {
      const { data, error, response } = await api.GET(path);
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data as T;
    },
    staleTime: 30_000,
    retry: false,
  });
}

export const useAdminIntegrations = () => useAdminRead<AdminIntegrationsPanel>("integrations", "/api/v1/admin/integrations");
export const useAdminBands = () => useAdminRead<AdminBandRow[]>("bands", "/api/v1/admin/bands");
export const useAdminSocials = () => useAdminRead<AdminSocialRow[]>("socials", "/api/v1/admin/socials");
export const useAdminPeople = () => useAdminRead<AdminPersonRow[]>("people", "/api/v1/admin/people");
export const useAdminPush = () => useAdminRead<AdminPushPanel>("push", "/api/v1/admin/push");
export const useAdminAnomalies = () => useAdminRead<AdminAnomaliesPanel>("anomalies", "/api/v1/admin/anomalies");
export function useAdminTutorials() {
  const q = useAdminRead<AdminTutorialDto[]>("tutorials", "/api/v1/admin/tutorials");
  return { ...q, data: q.data?.map(toTutorialRow) };
}

/** Add a tutorial (`tutorialId` absent) or replace one. A 422 names the field (title, YouTube link, audiences);
 * the form shows the server's message. Refetches the list after. */
export function useSaveAdminTutorial() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ tutorialId, input }: { tutorialId?: string; input: TutorialInput }) => {
      const { data, error, response } = tutorialId
        ? await api.PUT("/api/v1/admin/tutorials/{tutorialId}", { params: { path: { tutorialId } }, body: input })
        : await api.POST("/api/v1/admin/tutorials", { body: input });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return toTutorialRow(data.data);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["admin", "tutorials"] }),
  });
}

/** Delete a tutorial. Refetches the list after. */
export function useDeleteAdminTutorial() {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tutorialId: string) => {
      const { error, response } = await api.DELETE("/api/v1/admin/tutorials/{tutorialId}", {
        params: { path: { tutorialId } },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["admin", "tutorials"] }),
  });
}

/** "Sync now" on one band's job. Queues the job for the scheduler's next tick past the
 * release window and the minimum interval (never the daily budget); `reland` also forgets the stored payload hash so
 * an unchanged answer lands again. Refetches the band's jobs and the platform panel after. */
export function useForceAdminSync(bandId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ provider, kind, reland }: { provider: string; kind: string; reland: boolean }) => {
      const { data, error, response } = await api.POST("/api/v1/admin/bands/{bandId}/sync/{provider}/{kind}/force", {
        params: { path: { bandId, provider, kind }, query: { reland } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data as AdminBandSyncRow;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "band-sync", bandId] });
      void qc.invalidateQueries({ queryKey: ["admin", "integrations"] });
    },
  });
}

/** One band's sync jobs with every lifecycle column, including WHY a successful run fetched nothing (`lastNote`).
 * Off until a band is chosen. */
export function useAdminBandSync(bandId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["admin", "band-sync", bandId],
    enabled: !!bandId,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/admin/bands/{bandId}/sync", {
        params: { path: { bandId: bandId! } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data as AdminBandSync;
    },
    staleTime: 30_000,
    retry: false,
  });
}

/** Songstats requests per resource (artist or track) for one calendar month (`yyyy-MM`), against the vendor's
 * per-resource monthly limit. `month` undefined ⇒ the current UTC month. */
export function useAdminVendorUsage(month: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["admin", "vendor-usage", month ?? "current"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/admin/vendor-usage", {
        params: { query: month ? { month } : {} },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data as AdminVendorUsagePanel;
    },
    staleTime: 30_000,
    retry: false,
  });
}

// ── Applications (/apply review, .design-specs/apply.plan.md) ──────────────────────────────────────────────

export type AdminApplicationRow = components["schemas"]["AdminApplicationRow"];
export type AdminApplicationDetail = components["schemas"]["AdminApplicationDetail"];

/** The application list, optionally narrowed to one status. */
export function useAdminApplications(status: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["admin", "applications", status ?? "all"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/admin/applications", {
        params: { query: { status } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    staleTime: 15_000,
    retry: false,
  });
}

/** One application, everything the review screen shows. Off until one is chosen. */
export function useAdminApplication(applicationId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["admin", "application", applicationId],
    enabled: !!applicationId,
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/admin/applications/{applicationId}", {
        params: { path: { applicationId: applicationId! } },
      });
      if (!response.ok || !data?.data) throw new ApiError(response.status, error);
      return data.data;
    },
    retry: false,
  });
}

export type ApplicationReview =
  | { action: "approve"; name?: string; slug?: string }
  | { action: "waitlist" }
  | { action: "deny" }
  | { action: "notes"; notes: string };

/** Approve / waitlist / deny an application, or save its notes. Refetches the list and the detail after. */
export function useReviewApplication(applicationId: string) {
  const { api } = useApp();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (review: ApplicationReview) => {
      const path = { applicationId };
      const result =
        review.action === "approve"
          ? await api.POST("/api/v1/admin/applications/{applicationId}/approve", {
              params: { path },
              body: { name: review.name ?? null, slug: review.slug ?? null },
            })
          : review.action === "waitlist"
            ? await api.POST("/api/v1/admin/applications/{applicationId}/waitlist", { params: { path } })
            : review.action === "deny"
              ? await api.POST("/api/v1/admin/applications/{applicationId}/deny", { params: { path } })
              : await api.PUT("/api/v1/admin/applications/{applicationId}/notes", {
                  params: { path },
                  body: { notes: review.notes },
                });
      if (!result.response.ok || !result.data?.data) throw new ApiError(result.response.status, result.error);
      return result.data.data;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "applications"] });
      void qc.invalidateQueries({ queryKey: ["admin", "application", applicationId] });
    },
  });
}
